/* Feb-2026 · Sitemap ping card.
 *
 * Fires an admin-only ping to Google + Bing (`?sitemap=<url>`) so
 * the crawlers re-index within hours instead of weeks. Surfaces
 * the last 5 pings inline so ops sees confirmation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Search, RefreshCw, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { sitemapPingApi } from '@/lib/api';

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 1)  return 'just now';
    if (min < 60) return `${min} min ago`;
    const hrs = Math.round(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch { return '—'; }
}

export default function SitemapPingCard() {
  const [busy, setBusy]         = useState(false);
  const [refreshingLastmod, setRefreshingLastmod] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [runs, setRuns]         = useState([]);
  const [defaultUrl, setDefaultUrl] = useState('');
  const [indexnowReady, setIndexnowReady] = useState(false);
  const [override, setOverride] = useState('');
  const [selected, setSelected] = useState({ google: true, bing: true, indexnow: true });
  const [copied, setCopied]     = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await sitemapPingApi.history(5);
      setRuns(r.runs || []);
      setDefaultUrl(r.default_sitemap_url || '');
      setIndexnowReady(!!r.indexnow_configured);
    } catch { /* silent — panel just stays empty */ }
    finally { setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRefreshLastmods = async () => {
    setRefreshingLastmod(true);
    try {
      const r = await sitemapPingApi.refresh();
      toast.success(`Touched ${r.touched} SEO row(s) — /sitemap.xml lastmods are now fresh.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed.');
    } finally { setRefreshingLastmod(false); }
  };

  const onPing = async () => {
    const engines = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!engines.length) {
      toast.error('Pick at least one search engine.');
      return;
    }
    setBusy(true);
    try {
      const body = { engines };
      if (override.trim()) body.sitemap_url = override.trim();
      const r = await sitemapPingApi.ping(body);
      const failed = Object.entries(r.results).filter(([, v]) => !v.ok).map(([k]) => k);
      if (failed.length) {
        toast.error(`Ping partial — failed: ${failed.join(', ')}`);
      } else {
        toast.success(`Sitemap pinged: ${engines.join(', ')}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ping failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4 space-y-3"
         data-testid="sitemap-ping-card">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
            <Search className="h-4 w-4 text-primary" />
            SEO Sitemap Ping
          </div>
          <div className="text-[11px] text-muted-foreground">
            Ping Google + Bing to re-crawl <code className="font-mono text-[10.5px]">/sitemap.xml</code>{' '}
            after a big content push (launch flip, keyword refresh).
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={refreshing}
                data-testid="sitemap-ping-refresh-btn"
                className="h-8 gap-1 text-[12px]">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            Sitemap URL {defaultUrl && <span className="normal-case tracking-normal">(default: <code className="font-mono text-[10.5px]">{defaultUrl}</code>)</span>}
          </label>
          <Input value={override} onChange={(e) => setOverride(e.target.value)}
                  placeholder={defaultUrl || 'https://…/sitemap.xml'}
                  data-testid="sitemap-ping-override"
                  className="mt-1 h-8 font-mono text-[11.5px]" />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            Engines
          </div>
          <div className="mt-1 flex flex-wrap gap-2 text-[12px]">
            {['google', 'bing', 'indexnow'].map((e) => (
              <label key={e} className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-border bg-background/60 px-2 py-1">
                <input type="checkbox" checked={selected[e]}
                       onChange={(ev) => setSelected({ ...selected, [e]: ev.target.checked })}
                       data-testid={`sitemap-ping-toggle-${e}`} />
                <span className="capitalize">
                  {e}
                  {e !== 'indexnow' && <span className="ml-1 text-[9px] text-muted-foreground">(deprecated)</span>}
                  {e === 'indexnow' && !indexnowReady && <span className="ml-1 text-[9px] text-amber-700">(needs key)</span>}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10.5px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        <b>Note:</b> Google (Jun-2023) and Bing (2022) both retired their <code className="font-mono text-[10px]">?sitemap=</code> ping — the modern signal is a fresh <code className="font-mono text-[10px]">&lt;lastmod&gt;</code> on the sitemap. Hit <b>Refresh lastmods</b> after a content push, then IndexNow does the last-mile.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onPing} disabled={busy}
                data-testid="sitemap-ping-fire-btn"
                className="h-8 gap-1 text-[12px]">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Ping now
        </Button>
        <Button size="sm" variant="outline" onClick={onRefreshLastmods} disabled={refreshingLastmod}
                data-testid="sitemap-refresh-lastmods-btn"
                className="h-8 gap-1 text-[12px]">
          {refreshingLastmod ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh lastmods
        </Button>
        <span className="text-[10.5px] text-muted-foreground">
          Google/Bing legacy pings still fire (transparency). IndexNow drives Bing/Yandex.
        </span>
      </div>

      {runs.length > 0 && (
        <table className="w-full text-[11.5px]" data-testid="sitemap-ping-history">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="pb-1 text-left">When</th>
              <th className="pb-1 text-left">Engines</th>
              <th className="pb-1 text-center">Google</th>
              <th className="pb-1 text-center">Bing</th>
              <th className="pb-1 text-center">IndexNow</th>
              <th className="pb-1 text-left">Actor</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="py-1 pr-2">{fmtWhen(r.started_at)}</td>
                <td className="py-1 pr-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  {(r.engines || []).join(', ')}
                </td>
                {['google', 'bing', 'indexnow'].map((e) => {
                  const v = r.results?.[e];
                  if (!v) return <td key={e} className="py-1 text-center text-muted-foreground">—</td>;
                  return (
                    <td key={e} className="py-1 text-center">
                      {v.ok
                        ? <span className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-medium ${v.deprecated ? 'bg-slate-100 text-slate-700' : 'bg-emerald-100 text-emerald-800'}`}
                                title={v.deprecated ? 'Deprecated endpoint acknowledged' : 'ok'}>
                            {v.status_code || 'ok'}
                          </span>
                        : <span className="rounded-full bg-rose-100    px-1.5 py-0.5 text-[9.5px] font-medium text-rose-800"
                                title={v.error || `HTTP ${v.status_code}`}>
                            {v.status_code || 'fail'}
                          </span>}
                    </td>
                  );
                })}
                <td className="py-1 pr-2 text-[10.5px] text-muted-foreground">{r.actor || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Google Search Console submission helper ─────────────── */}
      <div className="mt-4 rounded-md border border-border bg-muted/20 p-3 space-y-2"
           data-testid="gsc-submit-section">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <Search className="h-3.5 w-3.5 text-primary" />
          Submit to Google Search Console
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Pinging is automated above. To <strong>register</strong> your sitemap in Google
          Search Console (needed once, gives you crawl stats), follow these 3 steps:
        </p>
        <ol className="text-[11px] text-muted-foreground space-y-1 list-none">
          <li className="flex gap-2">
            <span className="shrink-0 font-mono text-[10px] rounded bg-muted px-1.5 py-0.5 h-fit">1</span>
            Copy your sitemap URL below, then open GSC in a new tab.
          </li>
          <li className="flex gap-2">
            <span className="shrink-0 font-mono text-[10px] rounded bg-muted px-1.5 py-0.5 h-fit">2</span>
            In GSC → <strong>Sitemaps</strong> (left sidebar) → paste the URL → click <strong>Submit</strong>.
          </li>
          <li className="flex gap-2">
            <span className="shrink-0 font-mono text-[10px] rounded bg-muted px-1.5 py-0.5 h-fit">3</span>
            Done — GSC will show crawl status within 24 h.
          </li>
        </ol>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {/* Sitemap URL copy strip */}
          <code className="flex-1 min-w-0 truncate rounded bg-muted px-2 py-1 font-mono text-[10.5px] text-foreground"
                data-testid="gsc-sitemap-url">
            {(override.trim() || defaultUrl) || '(sitemap URL not configured)'}
          </code>
          <Button
            size="sm" variant="outline"
            disabled={!override.trim() && !defaultUrl}
            data-testid="gsc-copy-btn"
            className="h-7 gap-1 text-[11.5px] shrink-0"
            onClick={() => {
              const url = override.trim() || defaultUrl;
              if (!url) return;
              navigator.clipboard.writeText(url).then(() => {
                setCopied(true);
                toast.success('Sitemap URL copied to clipboard');
                setTimeout(() => setCopied(false), 2500);
              });
            }}
          >
            {copied
              ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
              : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy URL'}
          </Button>
          <a
            href="https://search.google.com/search-console/sitemaps"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gsc-open-btn"
          >
            <Button size="sm" className="h-7 gap-1 text-[11.5px]">
              <ExternalLink className="h-3 w-3" />
              Open GSC Sitemaps
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
